import { useEffect, useMemo } from 'react';
import type { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../core/select';

export type StateOption = {
  stateId: string;
  name: string;
  countryId: string | null;
};

export type StateRendererContext<TValues> = {
  field: ControllerRenderProps<TValues>;
  methods: UseFormReturn<TValues>;
};

export interface StateSelectFieldProps<TValues> extends StateRendererContext<TValues> {
  states: StateOption[];
  label: string;
  countryFieldName?: string;
  noneLabel?: string;
}

const SELECT_CLEAR_VALUE = '__dynamic_form_clear__';

export function StateSelectField<TValues>({
  field,
  methods,
  states,
  label,
  countryFieldName = 'countryId',
  noneLabel = 'None',
}: StateSelectFieldProps<TValues>) {
    // @ts-expect-error - countryFieldName is a string
  const selectedCountryId = methods.watch(countryFieldName) as string | undefined;

  const filteredStates = useMemo(() => {
    if (!selectedCountryId) {
      return [];
    }
    return states.filter((state) => state.countryId === selectedCountryId);
  }, [states, selectedCountryId]);

  useEffect(() => {
    if (!field.value) return;
    const isValid = filteredStates.some((state) => state.stateId === field.value);
    if (!isValid) {
      field.onChange(undefined);
    }
  }, [filteredStates, field]);

  const normalizedValue =
    field.value === null || field.value === undefined ? undefined : String(field.value);

  return (
    <Select
      value={normalizedValue}
      onValueChange={(value) => {
        if (value === SELECT_CLEAR_VALUE) {
          field.onChange(undefined);
          return;
        }
        field.onChange(value);
      }}
      disabled={!selectedCountryId}
    >
      <SelectTrigger className="w-full text-left">
        <SelectValue placeholder={selectedCountryId ? `Select ${label}` : 'Select a country first'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={SELECT_CLEAR_VALUE}>{noneLabel}</SelectItem>
        {filteredStates.map((state) => (
          <SelectItem key={state.stateId} value={state.stateId}>
            {state.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}












